import { precoComDescontoConfirmado, precoFinalSimulado, precoTabelaSimulado } from '@/utils/precoProveniencia';
import axios from '@/libs/axios';
import store from '@/store';
import Swal from 'sweetalert2';
import { selecionarNaturezaFiscal } from '@/utils/naturezaFiscal';

const _impostoCache = new Map();

export function getFirstSkuIdFromEmbalagens(embalagens) {
  if (!Array.isArray(embalagens)) return null;
  const skuKeys = [
    'itemExtrapq', 'itemPequeno', 'itemMedio', 'itemGrande', 'itemExtraGd',
    'itemSuperGd', 'itemUltraGd', 'itemUnico', 'itemOutros',
  ];
  for (const emb of embalagens) {
    const rows = emb?.grade?.dsHBIgrade?.ttHBIgrade;
    if (Array.isArray(rows)) {
      for (const row of rows) {
        for (const key of skuKeys) {
          const v = row?.[key];
          if (v) return v;
        }
      }
    }
  }
  return null;
}

/**
 * Endereço de entrega utilizável para simulação de imposto?
 * A TOTVS precisa de UF e cidade — sem eles o pwSimulaImpostoSF devolve tudo
 * zerado (sem erro), e o pedido segue com imposto/preço errados.
 */
export function enderecoEntregaValido(endereco) {
  if (!endereco) return false;
  const uf = String(endereco.ufid || '').trim();
  const cidade = String(endereco.clienteenderecocidade || '').trim();
  return uf.length > 0 && cidade.length > 0;
}

/**
 * Valida o endereço efetivo do cliente recém-selecionado (endereço de entrega
 * ou, na falta dele, o endereço do próprio cadastro) e exibe um sweet alert
 * quando UF/cidade faltam. Retorna true se o endereço é válido.
 * Usar em TODOS os pontos de seleção de cliente (Home, modais, Novo Pedido).
 */
export function avisarSeClienteSemEndereco(data) {
  const endereco = data?.enderecoEntrega || data?.cliente || null;
  if (enderecoEntregaValido(endereco)) return true;
  // Aviso NÃO-bloqueante: agora é possível escolher/cadastrar um endereço de
  // entrega (aba Entrega na operação; campo Endereço de Entrega no carrinho).
  // Retorna true para o fluxo seguir; o bloqueio real acontece no finalizar,
  // que exige endereço padrão válido OU endereço alternativo selecionado.
  Swal.fire({
    title: 'Cliente sem endereço cadastrado',
    text: 'O cliente selecionado não tem endereço completo (UF e cidade) no cadastro. Você pode continuar montando o pedido, mas antes de finalizar será preciso selecionar ou cadastrar um endereço de entrega (aba Entrega / campo Endereço de Entrega). Sem endereço, impostos e preços não calculam.',
    icon: 'warning',
    confirmButtonText: 'Entendi',
    customClass: { confirmButton: 'btn btn-warning' },
    buttonsStyling: false,
  });
  return true;
}

/**
 * Fluxo OBRIGATÓRIO na seleção de cliente sem endereço: o usuário precisa
 * informar/confirmar o endereço de entrega na hora (sem UF/cidade a TOTVS
 * devolve imposto e preço zerados). Retorna:
 *  - 'ok'       → cliente tem endereço válido, segue normal;
 *  - 'escolher' → usuário aceitou informar o endereço agora (abrir o modal);
 *  - 'trocar'   → usuário desistiu (limpar o cliente selecionado).
 */
export async function exigirEnderecoEntrega(data) {
  const endereco = data?.enderecoEntrega || data?.cliente || null;
  if (enderecoEntregaValido(endereco)) return 'ok';
  const r = await Swal.fire({
    title: 'Cliente sem endereço cadastrado',
    text: 'O cliente selecionado não tem endereço completo (UF e cidade) no cadastro. Sem endereço, impostos e preços não calculam — informe agora o endereço de entrega para continuar com este cliente.',
    icon: 'warning',
    showCancelButton: true,
    allowOutsideClick: false,
    confirmButtonText: 'Informar endereço',
    cancelButtonText: 'Trocar de cliente',
    customClass: { confirmButton: 'btn btn-primary', cancelButton: 'btn btn-outline-secondary ml-1' },
    buttonsStyling: false,
  });
  return r.isConfirmed ? 'escolher' : 'trocar';
}

/** O pedido do form é de DOAÇÃO (tipo WD)? Cobre o fluxo Home (tipoPedidoCliente) e Operações (dataCapaPedido). */
export function isPedidoDoacao(formData) {
  const tipo = formData?.tipoPedidoCliente?.tipopedidoid
    || formData?.dataCapaPedido?.tipopedidoid
    || formData?.tipoPedido?.tipopedidoid
    || '';
  return String(tipo).toUpperCase() === 'WD';
}

/**
 * Normaliza a resposta de POST /imposto/simular para pedidos de DOAÇÃO (WD):
 * Marca WD e seleciona PrecoDoacao, inclusive zero explícito.
 */
export function aplicarPrecoDoacao(data, doacao, tipoPedido = '') {
  if (!data || data._erro) return data;
  return { ...data, _pedidoDoacao: !!doacao, _tipoPedido: String(tipoPedido).toUpperCase() };
}

export async function simularImpostoUnitario({ formData, itemId, precoBase, comProveniencia = false }) {
  if (!itemId || precoBase == null) return null;
  const estabelecimentoid = formData?.estabelecimento?.estabelecimentoid || '';
  const clienteNomeAbreviado = formData?.cliente?.clientenomeabreviado || '';
  const tabelaprecoid = formData?.tabelaPreco?.tabelapreco_id || formData?.dataCapaPedido?.tabelaprecoid || '';
  const condicaopagamentoid = Number(formData?.condicaoPagamento?.condicaopagamentoid || 0);
  const naturezaoperacaoid = selecionarNaturezaFiscal(formData);
  // Canal de venda: cai para o canal do próprio cliente ou da capa do pedido
  // quando o form não tem o objeto canalVenda (ex.: cache antigo salvo antes do
  // lookup de canal, ou canal do ERP ausente em SLS_CanalVendas).
  const canal_venda_id = formData?.canalVenda?.canal_venda_id
    || formData?.cliente?.canalvendaid
    || formData?.cliente?.canal_venda_id
    || formData?.dataCapaPedido?.canalvendaid
    || '';
  const entrega = formData?.enderecoAlternativo || formData?.enderecoEntrega || {};
  const ufid = entrega.ufid || entrega.uf || '';
  const cidade = entrega.clienteenderecocidade || entrega.cidade || '';
  const isentoIE = formData?.cliente?.clienteisentoie ? 1 : 0;

  if (!estabelecimentoid || !clienteNomeAbreviado || !tabelaprecoid || (store.getters['preferencias/condicaoPagamentoAtiva'] && !condicaopagamentoid)) return null;

  const tipoPedido = formData?.tipoPedidoCliente?.tipopedidoid
    || formData?.dataCapaPedido?.tipopedidoid || formData?.tipoPedido?.tipopedidoid || '';
  const doacao = isPedidoDoacao(formData);

  const payload = {
    estabelecimentoid,
    clienteId: formData?.cliente?.clienteid,
    clienteNomeAbreviado,
    naturezaOperacaoId: naturezaoperacaoid,
    canalVendaId: canal_venda_id,
    itemId,
    quantidade: 1,
    precoUnitario: precoBase,
    ufDs: ufid,
    cidadeDs: cidade,
    contribuinteIcms: isentoIE ? 0 : 1,
    tabelaPrecoId: tabelaprecoid,
    condicaoPagamentoId: condicaopagamentoid,
  };
  // Reaproveitar apenas uma simulação concluída com os MESMOS dados fiscais.
  // Cliente, condição, endereço e tenant também podem mudar o preço final.
  const tenant = typeof localStorage !== 'undefined'
    ? (localStorage.getItem('dkpImpersonateSlug') || localStorage.getItem('tenantSlug') || process.env.VUE_APP_TENANT_SLUG || '')
    : '';
  const cacheKey = JSON.stringify([tenant, tipoPedido, payload]);
  if (_impostoCache.has(cacheKey)) {
    const entry = _impostoCache.get(cacheKey);
    if (Date.now() - entry.at < 60000) {
      return comProveniencia ? entry.calculo : entry.calculo.preco;
    }
    _impostoCache.delete(cacheKey);
  }

  try {
    // Simulação via backend (POST /api/imposto/simular), o MESMO caminho usado em
    // ModalEmbalagens e nos fluxos de pedido. O backend chama a TOTVS server-side
    // (rede interna), resolve item_id -> item_codigo_origem e usa Basic Auth.
    // Antes a chamada era feita direto do navegador para a URL da TOTVS, que em
    // produção é um host interno inalcançável (ERR_CONNECTION_TIMED_OUT) — cada
    // item travava ~minutos antes de cair no preço base.
    const { data: resp } = await axios.post('imposto/simular', payload, { timeout: 30000 });
    // Select by order type and the exact SKU's verified assessment flag.
    const dataResp = aplicarPrecoDoacao(resp?.data, doacao, tipoPedido);
    const descontoConfirmado = precoComDescontoConfirmado(dataResp);
    if (resp?.success === false || !dataResp || dataResp._erro) return null;
    const preco = precoFinalSimulado(dataResp);
    if (!Number.isFinite(preco) || preco <= 0) return null;
    const calculo = {
      preco,
      precoTabelaErp: precoTabelaSimulado(dataResp),
      // Preservar a cotação de doação também no retorno resumido/cache.
      // A exibição não pode inferi-la do preço de tabela ou do preço líquido.
      precoDoacao: dataResp.raw?.PrecoDoacao ?? dataResp.precoDoacao ?? null,
      descontoConfirmado,
    };
    if (Number.isFinite(calculo.precoTabelaErp) && calculo.precoTabelaErp > 0) {
      if (_impostoCache.size >= 240) _impostoCache.delete(_impostoCache.keys().next().value);
      _impostoCache.set(cacheKey, { at: Date.now(), calculo });
    }
    return comProveniencia ? calculo : preco;
  } catch (e) {
    console.error('Erro simularImpostoUnitario', e);
    return null;
  }
}

// Repetição e recuperação de carrinhos antigos: o preço histórico (inclusive
// precoUnitario) não é uma fonte válida para recalcular o imposto de hoje.
export async function simularPrecoAtualCarrinho(formData, itemId) {
  const tabela = formData?.tabelaPreco?.tabelapreco_id;
  if (!tabela || !itemId) throw new Error(`Selecione uma tabela de preços e confira o SKU ${itemId || 'sem código'}.`);
  let base;
  try {
    const { data } = await axios.get(
      `tabela-preco-item/tabelapreco/${encodeURIComponent(tabela)}/item/${encodeURIComponent(itemId)}`,
    );
    base = Number(data?.tabelapreco_item_preco ?? data?.tabelaprecoitempreco ?? data?.preco);
  } catch (error) {
    throw new Error(`Não foi possível consultar o preço atual do item ${itemId}. Tente novamente.`);
  }
  if (!Number.isFinite(base) || base <= 0) {
    throw new Error(`O item ${itemId} não tem preço vigente na tabela selecionada.`);
  }
  const resultado = await simularImpostoUnitario({
    formData, itemId, precoBase: base, comProveniencia: true,
  });
  if (!resultado || !Number.isFinite(Number(resultado.preco)) || Number(resultado.preco) <= 0) {
    throw new Error(`O cálculo fiscal do item ${itemId} não retornou preço válido (verifique também doações zeradas).`);
  }
  if (!Number.isFinite(Number(resultado.precoTabelaErp)) || Number(resultado.precoTabelaErp) <= 0
    || resultado.precoTabelaErp == null) {
    throw new Error(`O cálculo fiscal do item ${itemId} não retornou preço ERP de tabela válido.`);
  }
  return { base, preco: Number(resultado.preco), tabela: Number(resultado.precoTabelaErp) };
}

export function aplicarPrecoAtualCarrinho(item, calculo) {
  const quantidade = Number(item.pedido_item_quantidade);
  if (!Number.isFinite(quantidade) || quantidade <= 0) {
    throw new Error(`Confira a quantidade do item ${item.item_id}.`);
  }
  return {
    ...item,
    pedido_item_preco_unitario_base: calculo.base,
    pedido_item_preco_unitario: calculo.preco,
    precoComImposto: calculo.preco,
    pedido_item_preco_tabela_erp: calculo.tabela,
    precoDoacaoErp: String(item.categoria_tipo_pedido || '').toUpperCase() === 'WD' ? calculo.preco : item.precoDoacaoErp,
    valor_total: +(calculo.preco * quantidade).toFixed(2),
    pedido_item_preco_total: +(calculo.preco * quantidade).toFixed(2),
    valorTotalComImposto: +(calculo.preco * quantidade).toFixed(2),
  };
}
